import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Upload, Camera, ChevronDown, ChevronUp, Package, Loader2 } from 'lucide-react';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState({});
  const [formData, setFormData] = useState({
    name: '', contact_person: '', email: '', phone: '', address: '', notes: ''
  });
  const [pricelistFile, setPricelistFile] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);

  useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await api.getSuppliers();
      setSuppliers(response.data.suppliers);
    } catch (error) {
      toast.error('Kunde inte hämta leverantörer');
    } finally { setLoading(false); }
  };

  const fetchProducts = async (supplierId) => {
    try {
      const res = await api.getSupplierProducts(supplierId);
      setSupplierProducts(prev => ({ ...prev, [supplierId]: res.data.products || [] }));
    } catch (err) { console.error(err); }
  };

  const toggleExpand = (supplierId) => {
    if (expandedId === supplierId) {
      setExpandedId(null);
    } else {
      setExpandedId(supplierId);
      if (!supplierProducts[supplierId]) fetchProducts(supplierId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createSupplier(formData);
      toast.success('Leverantör tillagd!');
      setDialogOpen(false);
      setFormData({ name: '', contact_person: '', email: '', phone: '', address: '', notes: '' });
      fetchSuppliers();
    } catch (err) { toast.error('Kunde inte lägga till leverantör'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Är du säker? Detta tar även bort alla prislistor.')) return;
    try {
      await api.deleteSupplier(id);
      toast.success('Leverantör borttagen');
      fetchSuppliers();
    } catch (err) { toast.error('Kunde inte ta bort leverantör'); }
  };

  const handleUploadPricelist = async (e) => {
    e.preventDefault();
    if (!pricelistFile || !selectedSupplier) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pricelistFile);
      const response = await api.uploadPricelist(selectedSupplier.id, fd);
      toast.success(response.data.message);
      setUploadDialogOpen(false);
      setPricelistFile(null);
      if (expandedId === selectedSupplier.id) fetchProducts(selectedSupplier.id);
    } catch (err) { toast.error('Kunde inte ladda upp prislista'); }
    finally { setUploading(false); }
  };

  const handleScanReceipt = async (e) => {
    e.preventDefault();
    if (!receiptFile || !selectedSupplier) return;
    setScanning(true);
    setScanResult(null);
    try {
      const fd = new FormData();
      fd.append('file', receiptFile);
      const response = await api.scanReceipt(selectedSupplier.id, fd);
      setScanResult(response.data);
      toast.success(response.data.message);
      if (expandedId === selectedSupplier.id) fetchProducts(selectedSupplier.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kvittotolkning misslyckades');
    } finally { setScanning(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Leverantörer & Priser</h2>
          <p className="text-sm text-muted-foreground mt-1">Hantera leverantörer, prislistor och skanna kvitton</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="new-supplier-btn">
          <Plus size={14} className="mr-1.5" /> Ny leverantör
        </Button>
      </div>

      {suppliers.length === 0 ? (
        <div className="bg-card border border-border rounded p-8 text-center">
          <Package size={32} className="text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Inga leverantörer tillagda</p>
        </div>
      ) : (
        <div className="space-y-3">
          {suppliers.map((supplier) => {
            const isExpanded = expandedId === supplier.id;
            const products = supplierProducts[supplier.id] || [];
            return (
              <div key={supplier.id} className="bg-card border border-border rounded" data-testid={`supplier-${supplier.id}`}>
                <div onClick={() => toggleExpand(supplier.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/30 transition-colors cursor-pointer">
                  <div>
                    <p className="text-sm font-bold text-foreground">{supplier.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {supplier.contact_person || ''}{supplier.email ? ` · ${supplier.email}` : ''}{supplier.phone ? ` · ${supplier.phone}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedSupplier(supplier); setUploadDialogOpen(true); }}
                      data-testid={`upload-pricelist-${supplier.id}`}>
                      <Upload size={12} className="mr-1" /> Prislista
                    </Button>
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedSupplier(supplier); setScanDialogOpen(true); setScanResult(null); setReceiptFile(null); }}
                      data-testid={`scan-receipt-${supplier.id}`}>
                      <Camera size={12} className="mr-1" /> Skanna kvitto
                    </Button>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(supplier.id); }}
                      className="text-red-500 hover:text-red-700">
                      <Trash2 size={14} />
                    </Button>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-border">
                    {products.length === 0 ? (
                      <div className="p-6 text-center text-sm text-muted-foreground">Inga produkter registrerade. Ladda upp en prislista eller skanna ett kvitto.</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-muted/50 border-b border-border">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Produkt</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Art.nr</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-muted-foreground uppercase">Pris</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">Enhet</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {products.slice(0, 50).map((p, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2 text-sm">{p.product_name}</td>
                                <td className="px-4 py-2 text-sm text-muted-foreground">{p.article_number || '—'}</td>
                                <td className="px-4 py-2 text-sm text-right font-medium">{(p.price || 0).toLocaleString('sv-SE')} kr</td>
                                <td className="px-4 py-2 text-sm text-muted-foreground">{p.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {products.length > 50 && <p className="text-xs text-muted-foreground text-center py-2">Visar 50 av {products.length} produkter</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Supplier Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="add-supplier-dialog">
          <DialogHeader><DialogTitle>Lägg till leverantör</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Företagsnamn *</Label><Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} data-testid="supplier-name-input" /></div>
            <div><Label>Kontaktperson</Label><Input value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>E-post</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
              <div><Label>Telefon</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></div>
            </div>
            <div><Label>Adress</Label><Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} /></div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
              <Button type="submit" className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="create-supplier-submit-btn">Lägg till</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Pricelist Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ladda upp prislista — {selectedSupplier?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleUploadPricelist} className="space-y-4">
            <div>
              <Label>Välj fil (CSV eller Excel)</Label>
              <Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setPricelistFile(e.target.files[0])} required data-testid="pricelist-file-input" />
              <p className="text-xs text-muted-foreground mt-1">Kolumner: product_name, unit, price</p>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setUploadDialogOpen(false)}>Avbryt</Button>
              <Button type="submit" disabled={uploading} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white">
                {uploading ? 'Laddar upp...' : 'Ladda upp'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Scan Receipt Dialog */}
      <Dialog open={scanDialogOpen} onOpenChange={setScanDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="scan-receipt-dialog">
          <DialogHeader><DialogTitle>Skanna kvitto/faktura — {selectedSupplier?.name}</DialogTitle></DialogHeader>
          <form onSubmit={handleScanReceipt} className="space-y-4">
            <div>
              <Label>Ladda upp kvitto, faktura eller foto</Label>
              <div className="border-2 border-dashed border-border rounded p-4 text-center cursor-pointer hover:border-[hsl(16,100%,55%)] transition-colors"
                onClick={() => document.getElementById('receipt-input')?.click()}>
                <Camera size={24} className="mx-auto text-muted-foreground mb-1" />
                <p className="text-sm text-muted-foreground">{receiptFile ? receiptFile.name : 'Klicka för att välja fil'}</p>
                <p className="text-xs text-muted-foreground/60 mt-0.5">PDF, bilder — AI extraherar produkter & priser</p>
              </div>
              <input id="receipt-input" type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                onChange={(e) => setReceiptFile(e.target.files[0])} data-testid="receipt-file-input" />
            </div>
            {scanResult && (
              <div className="bg-green-50 border border-green-200 rounded p-3 space-y-2">
                <p className="text-sm font-medium text-green-800">{scanResult.message}</p>
                {scanResult.detected_supplier && <p className="text-xs text-green-700">Leverantör: {scanResult.detected_supplier}</p>}
                {scanResult.detected_total && <p className="text-xs text-green-700">Total: {scanResult.detected_total.toLocaleString('sv-SE')} kr</p>}
                {scanResult.extracted_products?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto">
                    {scanResult.extracted_products.map((p, i) => (
                      <p key={i} className="text-xs text-green-700">{p.product_name} — {p.price} kr/{p.unit}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setScanDialogOpen(false)}>Stäng</Button>
              <Button type="submit" disabled={scanning || !receiptFile} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="scan-receipt-submit">
                {scanning ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Skannar...</> : <><Camera size={14} className="mr-1.5" /> Skanna med AI</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
